import { useState } from "react"
import toast from "react-hot-toast"
import invariant from "tiny-invariant"
import { BusinessTypeEnum, JobTitleEnum } from "~/__generated__/graphql"
import { UPDATE_USER_MUTATION } from "~/api/updateUser"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { useHeaderText } from "~/common/HeaderContext"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { displayErrors } from "~/common/validations"
import { PublicMediaKitSetting } from "~/components/public-media-kit/PublicMediaKitSetting"
import {
  UserAccountForm,
  UserAccountFormValues,
} from "~/screens/users/components/UserAccountForm"
import { Card, CardContent } from "~/shadcn/ui/card"
import { ModalDialog } from "~/ui/ModalDialog"
import { trackEvent } from "~/util/tracking"
import { ChangePasswordModal } from "../modals/ChangePasswordModal"
import { UserDeleteButton } from "../users/components/UserDeleteButton"

const VIEW_SETTINGS_PROPS = { section: "account" }

export const SettingsAccountScreen = () => {
  useTrackScreenEvent("view_settings", VIEW_SETTINGS_PROPS)

  const currentUser = useCurrentUser()
  const [runUpdateUser, updateUserResult] =
    useSafeMutation(UPDATE_USER_MUTATION)

  useHeaderText({ header: "Account", subheader: null })

  const agency = currentUser.agency
  let publicMediaKitSetting: React.ReactNode = undefined
  if (agency?.canManagePublicMediaKit.value) {
    invariant(
      agency.publicMediaKitEnabled != null,
      "An agency's own members can read its publication setting"
    )
    publicMediaKitSetting = (
      <PublicMediaKitSetting
        agencyId={agency.id}
        agencySlug={agency.slug}
        enabled={agency.publicMediaKitEnabled}
      />
    )
  }

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false)

  const initialValues: UserAccountFormValues = {
    firstName: currentUser.firstName || "",
    lastName: currentUser.lastName || "",
    jobTitle: currentUser.jobTitle || "",
    notifyOfNewAgencies: currentUser.notifyOfNewAgencies,
    notifyOfNewCastingCalls: currentUser.notifyOfNewCastingCalls,
    notifyOfCastingCallSubmissions: currentUser.notifyOfCastingCallSubmissions,
    agency: {
      agentName: currentUser.agency?.agentName ?? "",
      contactEmail: currentUser.agency?.contactEmail ?? "",
      logo: undefined,
      name: currentUser.agency?.name ?? "",
      phone: currentUser.agency?.phone ?? "",
      website: currentUser.agency?.website ?? "",
      businessType: currentUser.agency?.businessType ?? "",
      displayInRosterDatabase:
        currentUser.agency?.displayInRosterDatabase ?? true,
    },
  }

  // The fields the user actually edited, for the update_account event.
  const changedFields = (values: UserAccountFormValues) =>
    Object.keys(values).filter(
      (field) =>
        JSON.stringify(values[field as keyof UserAccountFormValues]) !==
        JSON.stringify(initialValues[field as keyof UserAccountFormValues])
    )

  const onSubmit = async (values: UserAccountFormValues) => {
    const { data, errors } = await runUpdateUser({
      variables: {
        input: {
          firstName: values.firstName,
          lastName: values.lastName,
          jobTitle: (values.jobTitle || null) as JobTitleEnum | null,
          notifyOfNewAgencies: values.notifyOfNewAgencies,
          notifyOfNewCastingCalls: values.notifyOfNewCastingCalls,
          notifyOfCastingCallSubmissions: values.notifyOfCastingCallSubmissions,
          agency: {
            ...values.agency,
            businessType: currentUser.canEditBusinessType.value
              ? (values.agency.businessType as BusinessTypeEnum)
              : undefined,
          },
        },
      },
    })

    if (errors) {
      displayErrors(errors)
      console.error(errors)
    } else if (data) {
      trackEvent("update_account", {
        fields_changed: changedFields(values).join(", "),
      })
      toast.success("Account updated")
      setKey((key) => key + 1)
    } else {
      console.error("unexpected response", { data, errors })
    }
  }

  const [key, setKey] = useState(0)

  return (
    <div className="h-full pb-4 relative">
      <Card className="absolute left-0 right-0 top-0 bottom-4 overflow-auto">
        <CardContent className="flex-1 pt-4 flex flex-col">
          <UserAccountForm
            key={key}
            initialValues={initialValues}
            onSubmit={onSubmit}
            isLoading={updateUserResult.loading}
            error={updateUserResult.error}
            onChangePassword={() => setIsChangePasswordModalOpen(true)}
            email={currentUser.email ?? ""}
            logoThumbnailUrl={currentUser.agency?.logoThumbnailUrl ?? null}
            deleteButton={<UserDeleteButton userId={currentUser.id} />}
            ownAgencySettings={publicMediaKitSetting}
          />
          <ModalDialog
            open={isChangePasswordModalOpen}
            onClose={() => setIsChangePasswordModalOpen(false)}
          >
            <ChangePasswordModal
              onCloseModal={() => setIsChangePasswordModalOpen(false)}
            />
          </ModalDialog>
        </CardContent>
      </Card>
    </div>
  )
}
